import { useCallback, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';

import verificationIcon from '../../constants/images/modal-verification.svg';
import { verificationService } from '../../services/verification.service';
import styles from '../ModalReVerification/ModalReVerification.module.scss';

// Phone OTP verification, shown when the backend requires a verified phone to
// bid (status.phone_required) and the cleaner hasn't verified yet. Two steps:
// request a code, then confirm it. On success calls onVerified().
const ModalPhoneVerification = ({ initialPhone = '', onVerified }) => {
  const [phone, setPhone] = useState(initialPhone || '');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' | 'code'
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  const fieldStyle = {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid #d9d9e3',
    borderRadius: 8,
    fontSize: '1rem',
    marginBottom: 12,
    boxSizing: 'border-box',
  };

  const handleSend = useCallback(async () => {
    const trimmed = (phone || '').trim();
    if (!trimmed) {
      toast.error('Введіть номер телефону', { id: 'otp' });
      return;
    }
    setIsLoading(true);
    try {
      await verificationService.sendPhoneOtp(trimmed);
      setStep('code');
      toast.success('Код надіслано', { id: 'otp' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Не вдалося надіслати код. Спробуйте ще раз.', { id: 'otp' });
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [phone]);

  const handleVerify = useCallback(async () => {
    const trimmed = (phone || '').trim();
    const trimmedCode = (code || '').trim();
    if (!trimmedCode) {
      toast.error('Введіть код з SMS', { id: 'otp' });
      return;
    }
    setIsLoading(true);
    try {
      await verificationService.verifyPhoneOtp(trimmed, trimmedCode);
      setIsVerified(true);
      if (onVerified) onVerified();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Невірний код. Спробуйте ще раз.', { id: 'otp' });
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [phone, code, onVerified]);

  if (isVerified) {
    return (
      <div className={styles.modal}>
        <div className={styles.modal_content}>
          <img src={verificationIcon} alt="verification icon" />
          <h2>НОМЕР ПІДТВЕРДЖЕНО</h2>
          <p>Дякуємо! Ваш номер телефону підтверджено.</p>
        </div>
        <Toaster />
      </div>
    );
  }

  return (
    <div className={styles.modal}>
      <div className={styles.modal_content}>
        <img src={verificationIcon} alt="verification icon" />
        <h2>ПІДТВЕРДЖЕННЯ ТЕЛЕФОНУ</h2>
        <p>
          {step === 'phone'
            ? 'Щоб подавати заявки, підтвердіть свій номер телефону. Ми надішлемо код у SMS.'
            : `Введіть код, який ми надіслали на ${phone}.`}
        </p>

        {step === 'phone' ? (
          <>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+380XXXXXXXXX"
              style={fieldStyle}
              autoComplete="tel"
            />
            <button
              disabled={!phone.trim() || isLoading}
              onClick={handleSend}
              className={styles.modal_submit}
            >
              {isLoading ? 'Надсилання...' : 'Надіслати код'}
            </button>
          </>
        ) : (
          <>
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Код з SMS"
              style={fieldStyle}
              autoComplete="one-time-code"
            />
            <button
              disabled={!code.trim() || isLoading}
              onClick={handleVerify}
              className={styles.modal_submit}
            >
              {isLoading ? 'Перевірка...' : 'Підтвердити'}
            </button>
            <button
              type="button"
              disabled={isLoading}
              onClick={handleSend}
              style={{
                background: 'none',
                border: 'none',
                color: '#4b5bdc',
                cursor: 'pointer',
                marginTop: 10,
                fontSize: '0.9rem',
              }}
            >
              Надіслати код ще раз
            </button>
          </>
        )}
      </div>
      <Toaster />
    </div>
  );
};

export default ModalPhoneVerification;
